import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FLEET_STALE_AFTER_SECONDS, type RespostaDeMapa } from "@nxvolt/shared";
import "../styles/fleet-map.css";
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from "../lib/map-tiles.js";
import { fmtAge } from "../components/substation/bands.js";
/* O vocabulario de falha de carga: frase em portugues, sem o token, e com a
   FALHA decidida antes do VAZIO. Ver `lib/falha-de-carga.ts`. */
import {
  estadoDaLista,
  falhaDe,
  fraseDeFalhaDeCarga,
  type FalhaDeCarga,
} from "../lib/falha-de-carga.js";
import {
  celulaDeCampo,
  configuracaoDeFrota,
  estadoDeMovimento,
  FRASE_DO_MOVIMENTO,
  posicaoDoVeiculo,
  projetarFrota,
  ressalvaDeTruncamento,
  resumoDoMapa,
  ROTULO_DO_MOVIMENTO,
  contagemDaFrota,
  semSinalDaFrota,
  valorDeParcela,
  fraseDeCarimboFuturoNoMapa,
  fraseDePosicaoDesenhada,
  rotuloDoVeiculo,
  textoDaIdade,
  type EstadoDeMovimento,
  type VeiculoDeFrota,
} from "../components/fleet/aggregates.js";
import { KpiDeFrota } from "../components/fleet/FleetCells.js";

/**
 * Mapa ao Vivo da frota. Leaflet via CDN, um pin por veículo com posição
 * utilizável.
 *
 * ---------------------------------------------------------------------------
 * ESTA TELA ERA O PIOR DENOMINADOR DA ENTREGA. O que havia, medido:
 *
 *  - A SQL cortava `updated_at > NOW() - 10min` E `gps_lat IS NOT NULL`. Com 10
 *    de 16 veículos parados na origem, `pins` tinha ~6 e o cabeçalho anunciava
 *    **"6 veículos com GPS ativo"** numa topologia de 16. O denominador era
 *    quem respondeu.
 *  - `counts.stale = pins.length - fresh.length` só enxergava a faixa entre 2 e
 *    10 minutos: OS 10 DESAPARECIDOS NÃO ENTRAVAM EM CONTAGEM NENHUMA. Nem em
 *    "reportando", nem em "sem sinal".
 *  - `isStale` comparava contra `Date.now()`, o relógio do NAVEGADOR — operador
 *    com a máquina atrasada via a frota inteira fresca — e devolvia `false` para
 *    `NaN`, tratando carimbo ilegível como fresco.
 *  - `rodando: fresh.filter(p => p.ignition && (p.speed_kmh ?? 0) > 3)` — o
 *    `?? 0` transformava velocidade AUSENTE em "parado", que é afirmação e não
 *    default.
 *
 * AGORA: o corte de 10 min saiu da view (decisão do ADR — corte de idade ali
 * faria a queda do Portal VGPL ESVAZIAR o mapa em vez de degradá-lo), a idade vem
 * do servidor assinada, `isStale` não existe, e o cabeçalho declara sobre a
 * topologia com "sem sinal" e "sem posição" DISTINTOS. As três contagens
 * particionam os 16 — há invariante de teste varrendo o espaço.
 * ---------------------------------------------------------------------------
 */

/** Zoom 13 mostra São Paulo em nível de bairro. Abaixo disso vira mancha. */
const MIN_ZOOM_CIDADE = 13;
/** Teto na abertura: com a frota concentrada, enquadrar sem limite deixaria
 *  dois ônibus ocupando a tela inteira. */
const MAX_ZOOM_INICIAL = 15;
/** Vista inicial enquanto os pins não chegaram: centro de São Paulo. */
const SP_CENTRO: [number, number] = [-23.55, -46.63];

// Types minimos do Leaflet para nao precisar importar @types/leaflet
type LMap = {
  setView(coords: [number, number], zoom: number): LMap;
  fitBounds(bounds: [number, number][], opts?: { padding?: [number, number]; maxZoom?: number }): void;
  getBoundsZoom(bounds: [number, number][], inside?: boolean, padding?: [number, number]): number;
  addLayer(layer: unknown): void;
};
type LLayer = {
  addTo(map: LMap): LLayer;
  bindPopup(html: string): LLayer;
  setLatLng(coords: [number, number]): LLayer;
  openPopup?: () => void;
};
type LLayerGroup = { clearLayers(): void; addLayer(l: LLayer): void; addTo(m: LMap): LLayerGroup };
type LDivIcon = unknown;
type LGlobal = {
  map(el: HTMLElement | string): LMap;
  tileLayer(url: string, opts: { attribution: string; maxZoom?: number; subdomains?: string }): { addTo(m: LMap): void };
  divIcon(opts: { className: string; html: string; iconSize: [number, number]; iconAnchor: [number, number]; popupAnchor?: [number, number] }): LDivIcon;
  marker(coords: [number, number], opts: { icon: LDivIcon }): LLayer;
  layerGroup(): LLayerGroup;
};

/** Escape HTML minimo pra interpolar em popup Leaflet. Prefix e chassi viraram
 *  editaveis via /app/fleet, entao valores arbitrarios podem chegar aqui. Sem
 *  escape, um cadastro com `prefix = "<img src=x onerror=alert(1)>"` roda
 *  script no browser de quem abrir o popup (Stored XSS via popup HTML). */
function esc(s: unknown): string {
  const str = s === null || s === undefined ? "" : String(s);
  return str.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#39;";
      default: return c;
    }
  });
}

/** Lê um token `--nx-*` do :root em runtime. Leaflet precisa de string CSS
 *  concreta (não var()) porque desenha em SVG/canvas via atributo `stroke` /
 *  `fill`, e esses atributos não expandem custom properties.
 *  Fallback preserva o comportamento se o token for removido por engano. */
function readToken(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return raw || fallback;
}

/**
 * A cor do pin, por ESTADO CLASSIFICADO. `Record` exaustivo: estado novo não
 * compila até ganhar cor.
 *
 * Fallbacks em `rgb()` para não introduzir literais hex neste arquivo (a suíte
 * anti-regressão de cor faz grep por `#RRGGBB` em pages/**). Só são usados se o
 * token for removido do CSS por engano.
 *
 * OS MESMOS TOKENS DO BADGE, e é correção de um defeito de vocabulário: o pin
 * pintava "parado" de laranja e o badge pintava "sem sinal" de laranja. A mesma
 * cor significando duas coisas nas duas telas que o operador alterna — e a cor
 * só ajuda enquanto significa uma coisa só. Agora pin e badge lêem o mesmo
 * `--nx-state-*-ink`, e o `contrast.test.ts` prova que as seis tintas são
 * distintas entre si em cada tema.
 *
 * `indeterminado` e `sem-leitura` dividem a tinta neutra: os dois dizem "não
 * sei", e a diferença entre eles está no TEXTO do popup, que é o portador.
 * `carimbo-futuro` é a tinta de alerta — o dado aparenta ser o mais fresco de
 * todos e não pode ser situado no tempo.
 */
const COR_DO_PIN: Record<EstadoDeMovimento, () => string> = {
  "sem-leitura": () => readToken("--nx-state-unknown-ink", "rgb(51, 56, 73)"),
  "carimbo-futuro": () => readToken("--nx-state-clock-ink", "rgb(185, 28, 28)"),
  "sem-sinal": () => readToken("--nx-state-stale-ink", "rgb(163, 74, 8)"),
  carregando: () => readToken("--nx-state-charge-ink", "rgb(29, 78, 216)"),
  rodando: () => readToken("--nx-state-live-ink", "rgb(21, 128, 61)"),
  parado: () => readToken("--nx-state-idle-ink", "rgb(5, 12, 92)"),
  indeterminado: () => readToken("--nx-state-unknown-ink", "rgb(51, 56, 73)"),
};

/**
 * O pin mostra a ÚLTIMA posição, não a atual? `Record` exaustivo, não array.
 *
 * Era `readonly EstadoDeMovimento[]` com três nomes dentro, e um oitavo estado
 * entraria SEM degradação em silêncio — o pin apareceria em cor plena
 * afirmando posição atual sobre um estado que ninguém classificou. `Record`
 * não compila até o estado novo ter resposta.
 */
const MOSTRA_ULTIMA_POSICAO: Record<EstadoDeMovimento, boolean> = {
  "sem-leitura": true,
  "carimbo-futuro": true,
  "sem-sinal": true,
  carregando: false,
  rodando: false,
  parado: false,
  /* Indeterminado NÃO degrada: a leitura é corrente e a posição é de agora. O
     que não se sabe é o ESTADO do veículo, não onde ele está. */
  indeterminado: false,
};

/** Marcador vetorial em perspectiva. SVG em vez de emoji/imagem para preservar
 *  a mesma leitura em desktop, celular e telas de operação. */
function busMarkerSvgHtml(color: string, degradado: boolean): string {
  return `<svg class="nx-map-bus-marker__svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true">
    <ellipse cx="24" cy="38" rx="15" ry="4.4" fill="rgba(10, 18, 36, .28)"/>
    <g opacity="${degradado ? "0.54" : "1"}" transform="rotate(-8 24 24)">
      <path d="M10 13.5c0-2 1.7-3.5 3.7-3.5h17.8c2 0 3.7 1.6 3.7 3.5v17.3c0 1.7-1.4 3.1-3.1 3.1H13.1A3.1 3.1 0 0 1 10 30.8V13.5Z" fill="${color}" stroke="rgba(255,255,255,.94)" stroke-width="1.4"/>
      <path d="M30.6 10.2h.9c2 0 3.5 1.5 3.5 3.5v17.1c0 1.6-1.3 3-3 3h-3.7c1.6-1.1 2.4-2.7 2.4-4.8V14.5c0-1.7 0-3 .1-4.3Z" fill="rgba(7, 19, 38, .22)"/>
      <path d="M13.1 14.3c0-1 .8-1.8 1.8-1.8h15.3c1 0 1.8.8 1.8 1.8v7.2H13.1v-7.2Z" fill="rgba(232,244,255,.94)"/>
      <path d="M19.4 12.5v9M25.7 12.5v9" stroke="rgba(12, 31, 54, .34)" stroke-width="1.1"/>
      <path d="M10.9 26.2h23.2" stroke="rgba(7, 19, 38, .3)" stroke-width="1.2"/>
      <circle cx="16" cy="33" r="3.5" fill="rgb(13, 20, 34)"/><circle cx="29.4" cy="33" r="3.5" fill="rgb(13, 20, 34)"/>
      <circle cx="16" cy="33" r="1.25" fill="rgb(232, 239, 247)"/><circle cx="29.4" cy="33" r="1.25" fill="rgb(232, 239, 247)"/>
      <circle cx="13.9" cy="25.7" r="1.15" fill="rgba(255,255,255,.94)"/><circle cx="31.1" cy="25.7" r="1.15" fill="rgba(255,255,255,.94)"/>
    </g>
  </svg>`;
}

export function MapaFrotaPage() {
  const [searchParams] = useSearchParams();
  const mapRef = useRef<LMap | null>(null);
  const layerRef = useRef<LLayerGroup | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // fitBounds só na PRIMEIRA carga. Depois o operador pode ter dado zoom/pan
  // manual — polling a cada 10s nao pode resetar essa view.
  const didFitRef = useRef(false);
  const focusedVinRef = useRef<string | null>(null);
  const [data, setData] = useState<RespostaDeMapa | null>(null);
  const [error, setError] = useState<FalhaDeCarga | null>(null);
  const [erroDaBiblioteca, setErroDaBiblioteca] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [leafletReady, setLeafletReady] = useState(false);
  const focusVin = searchParams.get("focus");

  const cfg = useMemo(
    () => configuracaoDeFrota(data?.configuration?.staleAfterSeconds ?? FLEET_STALE_AFTER_SECONDS),
    [data?.configuration?.staleAfterSeconds],
  );
  const veiculos = useMemo(() => projetarFrota(data?.vehicles ?? []), [data?.vehicles]);
  const resumo = useMemo(() => resumoDoMapa(veiculos, cfg), [veiculos, cfg]);
  const semSinal = useMemo(() => semSinalDaFrota(contagemDaFrota(veiculos, cfg)), [veiculos, cfg]);
  /* O cabeçalho declara "N de M reportando" sobre as linhas que CHEGARAM. Se o
     `limit` truncou a resposta, M não é a topologia visível — e o envelope
     publica `totalVisivel` × `amostrados` justamente para isso ser dizível em
     vez de silencioso. */
  const truncamento =
    data === null ? null : ressalvaDeTruncamento(data.amostrados, data.totalVisivel);

  // Carregar CSS e JS do Leaflet uma vez
  useEffect(() => {
    if ((window as unknown as { L?: unknown }).L) {
      setLeafletReady(true);
      return;
    }
    const existing = document.getElementById("leaflet-js") as HTMLScriptElement | null;
    if (existing) {
      const onLoad = () => setLeafletReady(true);
      existing.addEventListener("load", onLoad);
      queueMicrotask(() => {
        if ((window as unknown as { L?: unknown }).L) setLeafletReady(true);
      });
      return () => existing.removeEventListener("load", onLoad);
    }
    const css = document.createElement("link");
    css.id = "leaflet-css";
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.id = "leaflet-js";
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.async = true;
    script.onload = () => setLeafletReady(true);
    /* ESTADO PRÓPRIO: a falha do Leaflet é de um RECURSO EXTERNO, não da
       consulta de frota. Fundi-la em `error` faria a tela dizer "seu acesso
       não alcança o mapa" quando o que faltou foi a biblioteca da CDN — e o
       vazio passaria a consultar uma falha que não é da lista. */
    script.onerror = () => setErroDaBiblioteca("O mapa não pôde ser desenhado: a biblioteca Leaflet não carregou (CDN bloqueada?).");
    document.body.appendChild(script);
  }, []);

  // Montar mapa quando Leaflet estiver disponivel.
  useEffect(() => {
    if (!leafletReady) return;
    const L = (window as unknown as { L?: LGlobal }).L;
    if (!L || !containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView(SP_CENTRO, MIN_ZOOM_CIDADE);
    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTRIBUTION,
      maxZoom: TILE_MAX_ZOOM,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
  }, [leafletReady]);

  /**
   * Carrega a frota pela ROTA DO MAPA.
   *
   * Eu tinha trocado para `/api/telemetria-frota` — a rota da tabela — para
   * garantir um denominador único. Estava errado, e o erro é de AUTORIZAÇÃO:
   * cada rota tem o seu `requireModule`, e `/api/telemetria-frota` gateia por
   * `telemetria_frota`. Um usuário com `mapa_frota` e SEM `telemetria_frota`
   * recebia 403 e o Mapa não abria — a tela perdia a permissão que ela própria
   * tem.
   *
   * A rota `/mapa` fica, com o gate dela, e passa a devolver a MESMA FORMA:
   * todos os visíveis, com `ageSeconds`, sem corte de idade. Preserva o gate
   * **e** o denominador único, que é o que se queria das duas vezes.
   */
  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const r = await fetch("/api/telemetria-frota/mapa", {
          credentials: "include",
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as RespostaDeMapa;
        if (controller.signal.aborted) return;
        setData(j);
        setError(null);
        setLoading(false);
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(falhaDe(e));
        setLoading(false);
      }
    };

    void load();
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, 10000);
    const onVis = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      controller.abort();
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  /**
   * Desenha os pins.
   *
   * `leafletReady` ESTÁ NAS DEPENDÊNCIAS, e a ausência dele era um defeito
   * MEDIDO nesta validação: com a resposta da API chegando ANTES do script da
   * CDN, este efeito rodava, caía no `return` cedo por `mapRef.current` ainda
   * ser nulo, e nunca mais rodava — `veiculos` é memoizado e estável, então
   * nenhuma dependência mudava depois. Resultado na tela: mapa montado, tiles
   * carregados, cabeçalho anunciando "6 de 16 reportando" e ZERO pins. A tela
   * afirmando uma frota que ela não desenhou.
   *
   * `leafletReady` muda de `false` para `true` quando o script carrega, e é
   * exatamente o sinal que faltava. A corrida existia na versão anterior pela
   * mesma razão e passava porque o fetch costumava demorar mais que a CDN.
   */
  useEffect(() => {
    if (!leafletReady) return;
    const L = (window as unknown as { L?: LGlobal }).L;
    if (!L || !mapRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    const bounds: [number, number][] = [];
    let focusedMarker: LLayer | null = null;
    let focusedPos: [number, number] | null = null;

    for (const v of veiculos) {
      /* A POSIÇÃO VEM DO CLASSIFICADOR. `posicaoDoVeiculo` recusa campo não
         fornecido pela fonte, carimbo à frente do relógio, coordenada fora de
         domínio e o `(0,0)` do GPS sem fix — quatro recusas que antes não
         existiam, e a última desenhava um ônibus de São Paulo no Golfo da
         Guiné. Veículo sem posição NÃO some da contagem: ele está em
         `resumo.semPosicao`, declarado no cabeçalho. */
      const posicao = posicaoDoVeiculo(v, cfg);
      if (posicao === null) continue;
      const movimento = estadoDeMovimento(v, cfg);
      const degradado = MOSTRA_ULTIMA_POSICAO[movimento];
      const cor = COR_DO_PIN[movimento]();
      const icon = L.divIcon({
        className: "nx-map-bus-marker",
        html: busMarkerSvgHtml(cor, degradado),
        iconSize: [48, 48],
        iconAnchor: [24, 38],
        popupAnchor: [0, -34],
      });
      const marker = L.marker(posicao, { icon });

      /* As três células vêm PRONTAS do classificador: valor, autorização de
         exibição e frase. O popup não decide nada — e por isso não pode mais
         imprimir "0 km/h" para velocidade ausente nem "— km" para autonomia
         impossível sem dizer que é impossível. */
      const linhas = (["soc_pct", "speed_kmh", "range_km"] as const)
        .map((campo) => {
          const celula = celulaDeCampo(v, campo, cfg, fmtAge);
          const rotulo = { soc_pct: "SOC", speed_kmh: "Velocidade", range_km: "Autonomia" }[campo];
          const unidade = { soc_pct: "%", speed_kmh: " km/h", range_km: " km" }[campo];
          const texto =
            celula.mostraValor && celula.valor !== null
              ? `${Number(celula.valor).toFixed(0)}${unidade}${
                  celula.sufixo ? ` (${celula.sufixo})` : ""
                }`
              : celula.sufixo
                ? `— (${celula.sufixo})`
                : "—";
          return `<div><dt>${esc(rotulo)}</dt><dd title="${esc(celula.frase)}">${esc(texto)}</dd></div>`;
        })
        .join("");

      /*
        O POPUP DO LEAFLET É BRANCO NOS DOIS TEMAS — ele não herda
        `--nx-bg`, porque o `.leaflet-popup-content-wrapper` traz fundo próprio.
        O banner usava `color-mix(var(--nx-warning) 15%, transparent)` sobre
        esse branco e media 1,92:1 nos dois temas.

        A correção é fixar o PAR INTEIRO — fundo E tinta do mesmo tema. Com os
        dois sólidos e vindos do mesmo par, a razão é a que `contrast.test.ts`
        mede (5,57:1 no claro, 7,54:1 no escuro) e NÃO depende do que está
        atrás. É exatamente a propriedade que o fundo translúcido destruía: ele
        deixava a razão à mercê da superfície, e a superfície aqui é um branco
        que não vira com o tema.

        Os valores vêm de `readToken` e não escritos à mão porque o Leaflet
        injeta HTML com atributo `style`, e atributo não expande `var(--nx-*)`.
      */
      const ressalva = degradado
        ? `<div style="background:${esc(
            readToken("--nx-state-stale-bg", "rgb(253, 247, 239)"),
          )};color:${esc(
            readToken("--nx-stale-ink", "rgb(163, 74, 8)"),
          )};padding:.25rem .4rem;border-radius:4px;font-size:.72rem;margin-bottom:.3rem">${esc(
            FRASE_DO_MOVIMENTO[movimento](v.sourceLabel),
          )}</div>`
        : "";

      marker.bindPopup(
        `<div class="nx-map-vehicle-popup">
           <div class="nx-map-vehicle-popup__head"><span class="nx-map-vehicle-popup__state" style="background:${esc(cor)}"></span><div><b>Ônibus ${esc(rotuloDoVeiculo(v))}</b><small>${esc(v.chassiVin)}</small></div></div>
           ${ressalva}
           <dl><div><dt>Estado</dt><dd style="color:${esc(cor)}">${esc(ROTULO_DO_MOVIMENTO[movimento])}</dd></div>${linhas}</dl>
           <p>${esc(textoDaIdade(v, cfg, fmtAge))} · Fonte: ${esc(v.sourceLabel)}</p>
         </div>`,
      );
      layerRef.current.addLayer(marker);
      bounds.push(posicao);
      if (focusVin === v.chassiVin) {
        focusedMarker = marker;
        focusedPos = posicao;
      }
    }

    if (focusedPos && focusVin !== focusedVinRef.current) {
      mapRef.current.setView(focusedPos, 16);
      focusedMarker?.openPopup?.();
      focusedVinRef.current = focusVin;
      didFitRef.current = true;
      return;
    }
    if (bounds.length > 0 && !didFitRef.current) {
      // O enquadramento puro abria em zoom 11 — região metropolitana — mesmo
      // com a frota toda dentro de ~11 km, porque o padding come muito de um
      // container de 680px. Quando o enquadramento natural ficaria largo
      // demais, centramos no meio da frota num zoom de cidade.
      const calculado = mapRef.current.getBoundsZoom(bounds, false, [40, 40]);
      if (calculado < MIN_ZOOM_CIDADE) {
        const lats = bounds.map((b) => b[0]);
        const lons = bounds.map((b) => b[1]);
        mapRef.current.setView(
          [
            (Math.min(...lats) + Math.max(...lats)) / 2,
            (Math.min(...lons) + Math.max(...lons)) / 2,
          ],
          MIN_ZOOM_CIDADE,
        );
      } else {
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: MAX_ZOOM_INICIAL });
      }
      didFitRef.current = true;
    }
  }, [veiculos, cfg, focusVin, leafletReady]);

  // Cleanup do mapa Leaflet no unmount para evitar leak de listeners + DOM
  // orfao quando trocar de rota.
  useEffect(() => {
    return () => {
      const m = mapRef.current as (LMap & { remove?: () => void }) | null;
      if (m && typeof m.remove === "function") m.remove();
      mapRef.current = null;
      layerRef.current = null;
      didFitRef.current = false;
      focusedVinRef.current = null;
    };
  }, []);

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Frota Elétrica</span>
          <h1>Mapa ao Vivo</h1>
          {/*
            O CABEÇALHO DECLARA SOBRE A TOPOLOGIA. `resumo.frase` é
            "6 de 16 reportando · 9 sem sinal · 1 sem posição", e as três
            contagens PARTICIONAM os 16 — antes dizia "6 veículos com GPS ativo"
            e os 10 restantes não apareciam em contagem nenhuma.
          */}
          <p className="nx-page-head__sub">
            {/*
              O SUBTÍTULO TAMBÉM CONSULTA A FALHA. `resumo.frase` deriva de
              `total === 0` e, com um pedido RECUSADO, escrevia "Nenhum ônibus
              visível para a fonte de telemetria ativa" ao lado da própria
              mensagem de 403 — a afirmação e a sua refutação na mesma linha.
            */}
            {loading
              ? "Carregando…"
              : error !== null
                ? fraseDeFalhaDeCarga(error, "o mapa da frota")
                : resumo.frase}
            {truncamento !== null && ` · ${truncamento}`}
            {erroDaBiblioteca !== null && ` · ${erroDaBiblioteca}`}
          </p>
        </div>
      </div>

      {/*
        OS CARDS. `indeterminado` ganhou card próprio: ele existia como
        categoria implícita e caía em "parados" por causa do `?? 0`, que
        transformava velocidade ausente em afirmação de imobilidade.
      */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: ".75rem",
          marginBottom: "var(--nx-space-4)",
        }}
        role="group"
        aria-label="Contagem por estado — soma os ônibus visíveis"
      >
        <CardDeEstado
          label="Rodando"
          total={resumo.total}
          valor={resumo.porMovimento.rodando}
          cor="var(--nx-state-live-ink)"
          hint={FRASE_DO_MOVIMENTO.rodando("")}
        />
        <CardDeEstado
          label="Carregando"
          total={resumo.total}
          valor={resumo.porMovimento.carregando}
          cor="var(--nx-state-charge-ink)"
          hint={FRASE_DO_MOVIMENTO.carregando("")}
        />
        <CardDeEstado
          label="Parados"
          total={resumo.total}
          valor={resumo.porMovimento.parado}
          cor="var(--nx-state-idle-ink)"
          hint={FRASE_DO_MOVIMENTO.parado("")}
        />
        <CardDeEstado
          label="Indeterminados"
          total={resumo.total}
          valor={resumo.porMovimento.indeterminado}
          cor="var(--nx-state-unknown-ink)"
          hint="Leitura corrente que não traz os campos que decidem o estado. Não é o mesmo que estar parado."
        />
        <CardDeEstado
          label="Sem sinal"
          total={resumo.total}
          valor={resumo.semSinal}
          cor="var(--nx-state-stale-ink)"
          /* A decomposição vem da MESMA partição que o número. Ela enumerava
             "16 com leitura vencida · 1 nunca reportaram" debaixo de um KPI
             dizendo 18: o décimo oitavo era o carimbo à frente do relógio, que
             entra em `semSinal` e não tinha balde na frase. */
          /* A segunda sentenca explica o PIN, e com a frota vazia nao ha pin a
             explicar: `semSinal.vazio` e o mesmo campo que faz o KPI da tabela
             imprimir travessao. */
          /*
            O HINT DEFINE A CATEGORIA, SEMPRE — e a falha mora no banner, não
            aqui.

            Duas armadilhas fechadas nesta posição:

            (1) Uma rodada atrás eu enfiei `fraseDeFalhaDeCarga` aqui — a frase
                de PERMISSÃO (403) fundida com a definição de "o que é um ônibus
                sem sinal". Hint de KPI responde "o que esta categoria
                significa", e isso não muda com um pedido recusado.

            (2) O ramo `semSinal.vazio` usava `semSinal.frase`, que com
                `total === 0` é **"Nenhum ônibus visível para a fonte ativa"** —
                afirmação sobre o conjunto VAZIO. Sob 403 o `total` é zero por
                AUSÊNCIA DE RESPOSTA, e o card imprimia essa afirmação como hint.
                Agora o ramo vazio DEFINE a categoria em vez de contar o
                conjunto: a definição vale com frota cheia, vazia ou recusada.

            A decomposição ("16 vencidos · 1 nunca reportou") só aparece quando
            HÁ sem-sinal a decompor; o resto é a definição estável da categoria.
          */
          hint={
            resumo.semSinal === null || resumo.semSinal === 0
              ? 'Reúne quem venceu o limiar, quem nunca reportou e quem tem carimbo à frente do relógio. O pin em cinza é a última posição conhecida, não a atual.'
              : `${semSinal.frase}. O pin em cinza mostra a última posição conhecida, que não é a posição atual.`
          }
        />
        <CardDeEstado
          label="Sem posição"
          total={resumo.total}
          valor={resumo.semPosicao}
          cor="var(--nx-mute-strong)"
          hint="Leitura corrente e sem coordenada utilizável. É diferente de estar sem sinal: aqui o ônibus está reportando, e o que falta é o GPS."
        />
      </div>

      {resumo.porMovimento["carimbo-futuro"] > 0 && (
        <p
          role="alert"
          style={{
            margin: "0 0 var(--nx-space-4)",
            padding: ".55rem .75rem",
            borderLeft: "3px solid var(--nx-state-clock-ink)",
            /* Par SÓLIDO do estado `carimbo-futuro`, o mesmo do pin e do badge.
               Era `--nx-danger` sobre `color-mix(... 8%, transparent)`: a tinta
               não vira com o tema (3,95:1 sobre o fundo escuro) e o fundo
               translúcido deixava a razão à mercê do que estivesse atrás. */
            background: "var(--nx-state-clock-bg)",
            color: "var(--nx-state-clock-ink)",
            fontSize: ".82rem",
            borderRadius: "var(--nx-radius-xs)",
          }}
        >
          {/* Frase pronta, com concordancia de NUMERO. Com um unico veiculo a
              tela dizia "1 de 18 onibus tem carimbo a frente e nao aparecem no
              mapa" — dois verbos plurais para um sujeito singular. */}
          {fraseDeCarimboFuturoNoMapa(resumo.porMovimento["carimbo-futuro"], resumo.total)}
        </p>
      )}

      <div
        ref={containerRef}
        role="application"
        /*
          O rótulo do mapa declara a MESMA coisa que o cabeçalho. Antes dizia
          "N veículos com GPS ativo" usando `pins.length`, ou seja, prometia ao
          leitor de tela um total que era só quem respondeu.
        */
        aria-label={
          error !== null
            ? `Mapa da frota. ${fraseDeFalhaDeCarga(error, "o mapa da frota")}`
            : `Mapa da frota. ${resumo.frase}. ${fraseDePosicaoDesenhada(resumo.comPosicao, resumo.total)}`
        }
        style={{
          height: "min(70vh, 640px)",
          width: "100%",
          borderRadius: "var(--nx-radius-lg)",
          border: "1px solid var(--nx-card-border)",
          background: "var(--nx-card-bg)",
        }}
      />
      {/*
        A FALHA VEM ANTES DO VAZIO — e a ordem É o conserto.

        Era `!loading && resumo.total === 0`, e com 403 a tela escrevia "Nenhum
        ônibus visível para a fonte de telemetria ativa" sobre um pedido que foi
        RECUSADO: não houve resposta para contar, e "nenhum" é afirmação sobre o
        mundo. Junto vinha `Error: HTTP 403` cru e um convite a investigar o
        serviço de sincronia, que não é a causa.

        É a mesma classe que o CCO acabou de fechar — estado de FALHA
        apresentado como estado de DADO. Ver `lib/falha-de-carga.ts`.
      */}
      {estadoDaLista({ carregando: loading, falha: error, total: resumo.total }) === "falha" && (
        <p
          role="alert"
          style={{ marginTop: "var(--nx-space-3)", color: "var(--nx-alert-ink)", fontSize: ".85rem" }}
        >
          {fraseDeFalhaDeCarga(error as FalhaDeCarga, "o mapa da frota")}
        </p>
      )}
      {estadoDaLista({ carregando: loading, falha: error, total: resumo.total }) === "vazio" && (
        <p style={{ marginTop: "var(--nx-space-3)", color: "var(--nx-mute)", fontSize: ".85rem" }}>
          Nenhum ônibus visível para a fonte de telemetria ativa. Se isto for inesperado,
          confira se o serviço de sincronia está classificando a procedência dos veículos.
        </p>
      )}
    </div>
  );
}

function CardDeEstado({
  label,
  valor,
  total,
  cor,
  hint,
}: {
  label: string;
  valor: number;
  /** A TOPOLOGIA. Sem ela o card não sabe distinguir os dois zeros. */
  total: number;
  cor: string;
  hint: string;
}) {
  /* Zero aqui significa "medido e é zero" e não "sem dado" — as seis contagens
     PARTICIONAM a topologia, e um balde vazio é informação. ENQUANTO HOUVER
     TOPOLOGIA: com `total === 0` o balde vazio deixa de ser informação sobre a
     frota e passa a ser uma afirmação sobre ônibus que não existem, que é o
     defeito do KPI "Sem sinal" nas outras duas telas. Ver `valorDeParcela`. */
  return (
    <KpiDeFrota label={label} valor={valorDeParcela(valor, total)} hint={hint} cor={cor} />
  );
}
